import { Box, Typography } from '@mui/material'
import React from 'react'
import NormalFieldPreview from './NormalFieldPreview'
import {
  DROPDOWN_DEFAULT_VALUE,
  FIELD_CONSTRAINTS,
  FIELD_TYPES,
  TABLE_CONTEXTS,
} from '../../../utility/constants'
import { getCommentId, getFieldId, getReviewId } from '../../../pages/DataEntryPage/utils/utils'
import TableFieldView from './TableFieldView'
import { useAuthContext } from '../../../hooks/useAuthContext'
import CustomView from '../../../custom_views/CustomView'
import { useResponsiveTypography } from '../../../utility/responsiveTypography'
import { formatUserFullName } from '../../../utility/userName'

const isConditionallyEnabled = (enableOn, operation, value) => {
  switch (operation) {
    case FIELD_CONSTRAINTS.EQUAL:
      return enableOn == value // we only checking value irrespective of type
    case FIELD_CONSTRAINTS.GT:
      return value > enableOn
    case FIELD_CONSTRAINTS.GTE:
      return value >= enableOn
    case FIELD_CONSTRAINTS.LT:
      return value < enableOn
    case FIELD_CONSTRAINTS.LTE:
      return value <= enableOn
    default:
      return false
  }
}

const isFieldVisible = (field, data, user) => {
  let visibility = true

  if (field?.is_hidden) {
    return false
  }

  // conditional enable disable
  if (field?.source_field_template_id && field?.enable_on_value) {
    const sourceFieldValue = data.data[getFieldId(field?.source_field_template_id)]

    const isConditionallyVisible = isConditionallyEnabled(
      field.enable_on_value,
      field.operation,
      sourceFieldValue,
    )

    if (!isConditionallyVisible) {
      return false
    }
  }

  // visible team types
  let isVisibleByType = null
  const visibleTeamTypes = field.visible_team_type?.filter((it) => it)
  if (visibleTeamTypes?.length > 0) {
    isVisibleByType = visibleTeamTypes?.some((vt) => user.team_types?.includes(vt))
  }

  // visible teams
  let isVisibleByTeam = true
  const visibleTeams = field.visible_team_id?.filter((it) => it)
  if (visibleTeams?.length > 0) {
    isVisibleByTeam = visibleTeams.some((vt) => user.teams?.includes(vt))
  }

  if (isVisibleByTeam != null && isVisibleByType != null) {
    return isVisibleByTeam || isVisibleByType
  } else if (isVisibleByTeam != null && isVisibleByType == null) {
    return isVisibleByTeam
  } else if (isVisibleByTeam == null && isVisibleByType != null) {
    return isVisibleByType
  }

  return visibility
}

const PagePreview = ({
  index,
  counts,
  page,
  fields,
  data,
  listData,
  dynamicListData,
  tableSubfieldMap,
  tableSequenceAndHeaders,
  activityType,
  processId,
  previewCurrency,
  isPreview = false,
  isDashboardPreview = false,
  defaultCurrency,
  externalValues = {},
  fieldMappings = {},
  fieldMetadata = {},
}) => {
  const { user } = useAuthContext()
  const { subheading } = useResponsiveTypography()

  return (
    <Box style={{ width: '100%' }} className="preview-root fieldset-legend">
      <fieldset
        className="fieldsetbox"
        style={{
          borderRadius: '20px',
          marginBottom: '10px',
        }}
        key={page.id}
      >
        <legend
          style={{
            width: '200px',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            top: '-20px',
            backgroundColor: '#AEADFA',
            padding: 0,
          }}
        >
          <div style={{ padding: '4px 8px', width: '100%' }}>
            <Typography className="textprimaryblue fontWeight700" sx={{ fontSize: subheading }}>
              Page {index + 1} of {counts}: {page.page_template_name}
            </Typography>
          </div>
        </legend>
        {fields
          ?.filter((it) => !it.is_subfield && isFieldVisible(it, data, user))
          ?.sort((a, b) => a.sequence - b.sequence)
          ?.map((ft, fIndex) => {
            if (ft.data_structure === FIELD_TYPES.TABLE) {
              // Handle table fields as usual
              const subfields = tableSubfieldMap
                .filter((tf) => tf.table_field_template_id == ft.id)
                .map((it) => fields.find((f) => f.id == it.subfield_template_id))
                .filter((it) => isFieldVisible(it, data, user))
              const customClass = 'preview-table-form'

              return (
                <Box key={ft.id} style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
                  <TableFieldView
                    field={ft}
                    value={data.data}
                    reviews={data.review_data}
                    comments={data.review_comments_data}
                    subfields={subfields.sort((a, b) => a.sequence - b.sequence)}
                    tableSequenceAndHeaders={
                      tableSequenceAndHeaders.find((it) => it.table_field_template_id == ft.id)
                        ?.sequence_header_map || []
                    }
                    listData={listData}
                    dynamicListData={dynamicListData}
                    activityType={activityType}
                    processId={processId}
                    data={data}
                    previewCurrency={previewCurrency}
                    isPreview={isPreview}
                    className={customClass}
                    defaultCurrency={defaultCurrency}
                    externalValues={externalValues}
                    fieldMappings={fieldMappings}
                    fieldMetadata={fieldMetadata}
                  />
                </Box>
              )
            } else if (ft.data_structure === FIELD_TYPES.LIST || ft.data_structure === FIELD_TYPES.LIST_MULTI) {
              return (
                <NormalFieldPreview
                  index={fIndex}
                  key={ft.id}
                  field={ft}
                  value={data.data[getFieldId(ft.id)]}
                  review={data.review_data[getReviewId(ft.id)]}
                  comment={data.review_comments_data[getCommentId(ft.id)]}
                  activityType={activityType}
                  previewCurrency={previewCurrency}
                  listData={listData}
                  dynamicListData={dynamicListData}
                  processId={processId}
                  data={data}
                  isPreview={isPreview}
                  defaultCurrency={defaultCurrency}
                  externalValues={externalValues}
                  fieldMappings={fieldMappings}
                  fieldMetadata={fieldMetadata}
                />
              )
            } else if (ft.data_structure === FIELD_TYPES.CUSTOM_VIEW) {
              return (
                <CustomView
                  id={ft.id}
                  data={{
                    process_reference_num: data.process_reference_num,
                    process_template_id: data.process_template_id,
                    status: data.status,
                    prefix: data.prefix,
                    version: data.data.version,
                    tableContext: TABLE_CONTEXTS.DATA_PREVIEW_VIEW,
                    value: data.data,
                  }}
                  config={ft.metadata}
                  isPreview={isPreview}
                  isDashboardPreview={isDashboardPreview}
                  fields={fields}
                  processId={processId}
                  activityTypes={activityType}
                />
              )
            } else {
              // Handle other field types as usual
              return (
                <NormalFieldPreview
                  listData={listData}
                  dynamicListData={dynamicListData}
                  processId={processId}
                  index={fIndex}
                  key={ft.id}
                  field={ft}
                  value={data.data[getFieldId(ft.id)]}
                  review={data.review_data[getReviewId(ft.id)]}
                  comment={data.review_comments_data[getCommentId(ft.id)]}
                  activityType={activityType}
                  previewCurrency={previewCurrency}
                  data={data}
                  isPreview={isPreview}
                  defaultCurrency={defaultCurrency}
                  externalValues={externalValues}
                  fieldMappings={fieldMappings}
                  fieldMetadata={fieldMetadata}
                />
              )
            }
          })}
      </fieldset>
    </Box>
  )
}

export default PagePreview
